import {
  REWARDED_STATE_STORAGE_KEY,
  REWARDED_STATE_VERSION,
} from '../config/rewardedAds';

export type RewardedPersistState = {
  version: number;
  nextAttractionDiscount: boolean;
  landDiscountParcelId: string | null;
  autoCollectUntil: number | null;
  lastRewardedAt: number | null;
};

const EMPTY: RewardedPersistState = {
  version: REWARDED_STATE_VERSION,
  nextAttractionDiscount: false,
  landDiscountParcelId: null,
  autoCollectUntil: null,
  lastRewardedAt: null,
};

let memoryState: RewardedPersistState = { ...EMPTY };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function emptyRewardedState(): RewardedPersistState {
  return { ...EMPTY };
}

export function parseRewardedState(raw: string | null | undefined): RewardedPersistState {
  if (!raw) {
    return emptyRewardedState();
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return emptyRewardedState();
    }
    return {
      version: REWARDED_STATE_VERSION,
      nextAttractionDiscount: parsed.nextAttractionDiscount === true,
      landDiscountParcelId:
        typeof parsed.landDiscountParcelId === 'string' && parsed.landDiscountParcelId.length > 0
          ? parsed.landDiscountParcelId
          : null,
      autoCollectUntil:
        typeof parsed.autoCollectUntil === 'number' && Number.isFinite(parsed.autoCollectUntil)
          ? parsed.autoCollectUntil
          : null,
      lastRewardedAt:
        typeof parsed.lastRewardedAt === 'number' && Number.isFinite(parsed.lastRewardedAt)
          ? parsed.lastRewardedAt
          : null,
    };
  } catch {
    return emptyRewardedState();
  }
}

function readStorage(): string | null {
  try {
    return globalThis.localStorage?.getItem(REWARDED_STATE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(raw: string): void {
  try {
    globalThis.localStorage?.setItem(REWARDED_STATE_STORAGE_KEY, raw);
  } catch {
    // Memory copy remains the session source of truth.
  }
}

export function loadRewardedState(): RewardedPersistState {
  const stored = parseRewardedState(readStorage());
  memoryState = stored;
  return { ...memoryState };
}

export function getRewardedState(): RewardedPersistState {
  return { ...memoryState };
}

export function saveRewardedState(next: RewardedPersistState): RewardedPersistState {
  memoryState = {
    version: REWARDED_STATE_VERSION,
    nextAttractionDiscount: next.nextAttractionDiscount === true,
    landDiscountParcelId: next.landDiscountParcelId ?? null,
    autoCollectUntil: next.autoCollectUntil ?? null,
    lastRewardedAt: next.lastRewardedAt ?? null,
  };
  writeStorage(JSON.stringify(memoryState));
  return { ...memoryState };
}

export function updateRewardedState(
  patch: Partial<Omit<RewardedPersistState, 'version'>>,
): RewardedPersistState {
  return saveRewardedState({
    ...getRewardedState(),
    ...patch,
  });
}

export function resetRewardedStateForTests(): void {
  memoryState = emptyRewardedState();
  try {
    globalThis.localStorage?.removeItem(REWARDED_STATE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
